var __jsx = React.createElement;
import React, { useRef, useState, useEffect } from "react";
import { useLang } from "@/common/Contexts/LanguageContext";
import useIsMobile from "@hooks/useIsMobile";
import { trackEvents } from "@/common/events/events";
export function PriceFilterChips(_ref) {
  var ranges = _ref.ranges,
    selectedRange = _ref.selectedRange,
    onRangeSelect = _ref.onRangeSelect,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? "" : _ref$className;
  var scrollContainerRef = useRef(null);
  var lang = useLang();
  var isRTL = lang === "ar";
  var isMobile = useIsMobile();
  var _useState = useState(isRTL),
    canScrollLeft = _useState[0],
    setCanScrollLeft = _useState[1];
  var _useState2 = useState(!isRTL),
    canScrollRight = _useState2[0],
    setCanScrollRight = _useState2[1];

  // Check scroll state
  var checkScrollState = function checkScrollState() {
    if (!scrollContainerRef.current) return;
    var _scrollContainerRef$c = scrollContainerRef.current,
      scrollLeft = _scrollContainerRef$c.scrollLeft,
      scrollWidth = _scrollContainerRef$c.scrollWidth,
      clientWidth = _scrollContainerRef$c.clientWidth;
    var maxScroll = scrollWidth - clientWidth;

    // No scroll needed if content fits
    if (maxScroll <= 0) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    // For RTL, scrollLeft values are typically negative or work differently across browsers
    // We need to normalize the scroll position
    if (isRTL) {
      // In RTL, different browsers handle scrollLeft differently
      // Most modern browsers: scrollLeft goes from 0 to negative
      var normalizedScroll = Math.abs(scrollLeft);

      // Can scroll left (backwards in RTL) if we're not at the end
      setCanScrollLeft(normalizedScroll < maxScroll - 1);
      // Can scroll right (forward in RTL) if we're not at the start
      setCanScrollRight(normalizedScroll > 1);
    } else {
      // LTR behavior
      setCanScrollLeft(scrollLeft > 1);
      setCanScrollRight(scrollLeft < maxScroll - 1);
    }
  };

  // Scroll functions - left button always scrolls left, right button always scrolls right
  var scrollLeft = function scrollLeft() {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: -200,
      behavior: "smooth"
    });
  };
  var scrollRight = function scrollRight() {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: 200,
      behavior: "smooth"
    });
  };

  // Set initial state when component mounts or direction changes
  useEffect(function () {
    if (!scrollContainerRef.current || !ranges.length) return;
    var container = scrollContainerRef.current;
    var scrollWidth = container.scrollWidth,
      clientWidth = container.clientWidth;
    var hasScroll = scrollWidth > clientWidth;
    if (!hasScroll) {
      // No scrolling needed
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    // Set initial visibility - show forward scroll button for both directions
    if (isRTL) {
      // For RTL: show left arrow initially (scrolls forward in RTL)
      setCanScrollLeft(true);
      setCanScrollRight(false);
    } else {
      // For LTR: show right arrow initially (scrolls forward in LTR)
      setCanScrollLeft(false);
      setCanScrollRight(true);
    }

    // Both start at position 0
    container.scrollLeft = 0;
  }, [isRTL, ranges]);

  // Add resize observer to check scroll state when container size changes
  useEffect(function () {
    var resizeObserver = new ResizeObserver(function () {
      // Only check scroll state on resize if user has already scrolled
      // to preserve initial state
      if (scrollContainerRef.current) {
        var _scrollLeft = scrollContainerRef.current.scrollLeft;
        if (Math.abs(_scrollLeft) > 0) {
          checkScrollState();
        }
      }
    });
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }
    return function () {
      resizeObserver.disconnect();
    };
  }, []);
  var getChipText = function getChipText(range) {
    if (range.text) {
      return "".concat(range.text, " ").concat(range.range);
    } else {
      return range.range;
    }
  };
  var isSelected = function isSelected(range) {
    return selectedRange && selectedRange.from === range.from && selectedRange.to === range.to;
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "relative ".concat(className)
  }, canScrollLeft && !isMobile && /*#__PURE__*/React.createElement("div", {
    onClick: scrollLeft,
    className: "cursor-pointer  absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-50 transition-colors [box-shadow:1px_2px_3.5px_1px_#00000021]",
    "aria-label": isRTL ? "Scroll forward" : "Scroll back"
  }, /*#__PURE__*/React.createElement("svg", {
    className: "w-4 h-4 text-black",
    fill: "none",
    stroke: "currentColor",
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 2,
    d: "M15 19l-7-7 7-7"
  }))), canScrollRight && !isMobile && /*#__PURE__*/React.createElement("div", {
    onClick: scrollRight,
    className: "cursor-pointer  absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-50 transition-colors [box-shadow:1px_2px_3.5px_1px_#00000021]",
    "aria-label": isRTL ? "Scroll back" : "Scroll forward"
  }, /*#__PURE__*/React.createElement("svg", {
    className: "w-4 h-4 text-black",
    fill: "none",
    stroke: "currentColor",
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 2,
    d: "M9 5l7 7-7 7"
  }))), /*#__PURE__*/React.createElement("div", {
    ref: scrollContainerRef,
    dir: isRTL ? "rtl" : "ltr",
    className: "flex gap-2 overflow-x-auto ".concat(isMobile ? 'px-4' : 'px-10', " py-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] cursor-pointer"),
    onScroll: checkScrollState
  }, ranges.map(function (range, index) {
    var selected = isSelected(range);
    return /*#__PURE__*/React.createElement("button", {
      key: "".concat(range.from, "-").concat(range.to, "-").concat(index),
      onClick: function onClick() {
        if (!selected) {
          onRangeSelect(range);
          trackEvents("finance_filter_search", {
            "filter_range": (range === null || range === void 0 ? void 0 : range.from) + "-" + (range === null || range === void 0 ? void 0 : range.to)
          });
        }
      },
      className: "\n                 ".concat(selected ? 'cursor-default' : 'cursor-pointer', " flex items-center justify-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all duration-200 flex-shrink-0 min-w-fit\n                ").concat(selected ? "bg-[#3c56d4] text-white border border-[#3c56d4] shadow-sm" : "bg-white text-[#3c56d4] border border-[#3c56d4]/20 hover:border-[#3c56d4]/30 hover:bg-gray-50 backdrop-blur-sm", "\n              ")
    }, /*#__PURE__*/React.createElement("span", {
      className: "text-sm font-medium"
    }, getChipText(range)), /*#__PURE__*/React.createElement("div", {
      className: "w-3 h-3 flex-shrink-0 flex items-center justify-center"
    }, /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 13 14",
      fill: "none",
      className: "w-full h-full"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M7.81866 12.3001C7.59449 12.7917 7.44631 13.3251 7.38954 13.8845L12.1334 12.8873C12.3576 12.3958 12.5056 11.8623 12.5625 11.3029L7.81866 12.3001Z",
      fill: "currentColor"
    }), /*#__PURE__*/React.createElement("path", {
      d: "M12.133 9.89946C12.3572 9.40802 12.5053 8.87447 12.562 8.31507L8.8668 9.09229V7.59819L12.1329 6.91181C12.357 6.42037 12.5052 5.88682 12.562 5.32742L8.86669 6.10398V0.730732C8.30046 1.04513 7.79759 1.46364 7.38882 1.95729V6.41473L5.91094 6.72538V-7.36415e-05C5.34471 0.314218 4.84184 0.732832 4.43307 1.22648V7.03591L1.12631 7.7308C0.902146 8.22224 0.753856 8.75579 0.696976 9.31519L4.43307 8.53001V10.4116L0.429115 11.253C0.204947 11.7444 0.0567683 12.278 0 12.8374L4.19102 11.9566C4.53219 11.8864 4.82542 11.687 5.01606 11.4125L5.78467 10.2856V10.2854C5.86445 10.1688 5.91094 10.0282 5.91094 9.87681V8.21937L7.38882 7.90872V10.8969L12.1329 9.89924L12.133 9.89946Z",
      fill: "currentColor"
    }))));
  })));
}
export default PriceFilterChips;